import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase, supabaseHelpers, UserProfile, UserStats } from '../lib/supabase';

interface AuthContextType {
  user: any;
  userProfile: UserProfile | null;
  userStats: UserStats | null;
  loading: boolean;
  darkMode: boolean;
  setDarkMode: (darkMode: boolean) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string, isFreeSignup?: boolean) => Promise<void>;
  signOut: () => Promise<void>;
  updateUserProfile: (profile: Partial<UserProfile>) => Promise<void>;
  updateUserStats: (stats: Partial<UserStats>) => Promise<UserStats | undefined>;
  refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<any>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    // Initialize from localStorage
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  // Persist dark mode to localStorage
  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);



  const refreshUserData = useCallback(async (userToRefresh?: any) => {
    const currentUser = userToRefresh || user;
    if (currentUser && currentUser.id) {
      try {
        console.log('Refreshing user data for user:', currentUser.id);
        
        // Add timeout to prevent hanging - increased for Safari compatibility
        const timeout = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Database timeout after 30 seconds')), 30000)
        );
        
        // Add retry logic for Safari compatibility
        const fetchWithRetry = async (fetchFn: () => Promise<any>, retries = 3) => {
          for (let i = 0; i < retries; i++) {
            try {
              return await fetchFn();
            } catch (error) {
              console.warn(`Database fetch attempt ${i + 1} failed:`, error);
              if (i === retries - 1) throw error;
              // Wait before retry
              await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
            }
          }
        };

        const [profile, stats] = await Promise.race([
          Promise.all([
            fetchWithRetry(() => supabaseHelpers.getUserProfile(currentUser.id)),
            fetchWithRetry(() => supabaseHelpers.getUserStats(currentUser.id))
          ]),
          timeout
        ]) as [any, any];
        
        console.log('Retrieved profile:', profile);
        console.log('Retrieved stats:', stats);
        
        // If profile or stats don't exist, create them
        if (!profile) {
          console.log('Profile not found, creating new profile...');
          const isFreeSignup = currentUser.user_metadata?.signup_type === 'free';
          const defaultState = typeof localStorage !== 'undefined' ? localStorage.getItem('selectedState') || 'ga' : 'ga';
          const newProfile = await supabaseHelpers.upsertUserProfile({
            user_id: currentUser.id,
            name: currentUser.user_metadata?.name || 'User',
            email: currentUser.email || '',
            subscription_type: isFreeSignup ? 'free' : 'trial',
            state: defaultState,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
          setUserProfile(newProfile);
        } else {
          // If user signed up as free but profile was created with trial (e.g. by DB trigger), fix it
          const isFreeSignup = currentUser.user_metadata?.signup_type === 'free';
          if (isFreeSignup && profile.subscription_type !== 'free') {
            const updatedProfile = await supabaseHelpers.upsertUserProfile({
              ...profile,
              user_id: currentUser.id,
              subscription_type: 'free',
              updated_at: new Date().toISOString()
            });
            setUserProfile(updatedProfile);
          } else {
            setUserProfile(profile);
          }
        }
        
        if (!stats) {
          console.log('Stats not found, creating new stats...');
          const newStats = await supabaseHelpers.upsertUserStats({
            user_id: currentUser.id,
            total_questions: 500,
            questions_answered: 0,
            correct_answers: 0,
            incorrect_answers: 0,
            current_streak: 0,
            max_streak: 0,
            accuracy: 0,
            progress: 0,
            daily_goal: 20,
            daily_progress: 0,
            weekly_accuracy: 0,
            categories: {},
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
          setUserStats(newStats);
        } else {
          // Check if we need to reset daily progress (new day)
          const lastUpdated = new Date(stats.updated_at);
          const today = new Date();
          const isNewDay = lastUpdated.getDate() !== today.getDate() || 
                          lastUpdated.getMonth() !== today.getMonth() || 
                          lastUpdated.getFullYear() !== today.getFullYear();
          
          if (isNewDay && stats.daily_progress > 0) {
            console.log('New day detected, resetting daily progress');
            const resetStats = {
              ...stats,
              daily_progress: 0,
              updated_at: new Date().toISOString()
            };
            const updatedStats = await supabaseHelpers.upsertUserStats(resetStats);
            setUserStats(updatedStats);
          } else {
            setUserStats(stats);
          }
        }
      } catch (error) {
        console.error('Error refreshing user data:', error);
        // Don't throw - set minimal profile/stats so dashboard still loads instead of hanging
        console.warn('Continuing with minimal profile due to error');
        const isFreeSignup = currentUser.user_metadata?.signup_type === 'free';
        const defaultState = typeof localStorage !== 'undefined' ? localStorage.getItem('selectedState') || 'ga' : 'ga';
        setUserProfile({
          user_id: currentUser.id,
          name: currentUser.user_metadata?.name || currentUser.email || 'User',
          email: currentUser.email || '',
          subscription_type: isFreeSignup ? 'free' : 'trial',
          state: defaultState,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
        setUserStats({
          user_id: currentUser.id,
          total_questions: 500,
          questions_answered: 0,
          correct_answers: 0,
          incorrect_answers: 0,
          current_streak: 0,
          max_streak: 0,
          accuracy: 0,
          progress: 0,
          daily_goal: 20,
          daily_progress: 0,
          weekly_accuracy: 0,
          categories: {},
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
      }
    } else {
      console.log('No user available for refreshUserData');
      return; // Silently return - don't throw; user may be loading or signed out
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Remove user dependency to prevent infinite loops

  const signIn = async (email: string, password: string) => {
    try {
      console.log('Starting sign in process...');
      
      // Clear only our app-specific data before signing in
      console.log('Clearing app-specific data before sign in...');
      localStorage.removeItem('realestart_user_stats');
      localStorage.removeItem('realestart_user_mode');
      localStorage.removeItem('realestart_user_profile');
      localStorage.removeItem('realestart_user_id');
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        console.error('Supabase auth error:', error);
        throw error;
      }
      
      console.log('Sign in successful, user:', data.user?.id);
      
      if (data.user) {
        setUser(data.user);
        
        // Add timeout for refreshUserData to prevent hanging
        try {
          const refreshPromise = refreshUserData(data.user);
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('User data refresh timeout')), 15000)
          );
          
          await Promise.race([refreshPromise, timeoutPromise]);
          console.log('User data refreshed successfully');
        } catch (refreshError) {
          console.warn('User data refresh failed, continuing with limited functionality:', refreshError);
          // Don't throw - allow user to continue with limited functionality
        }
      }
    } catch (error) {
      console.error('Error signing in:', error);
      throw error;
    }
  };

  const signUp = async (email: string, password: string, name: string, isFreeSignup = false) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
            ...(isFreeSignup && { signup_type: 'free' })
          }
        }
      });
      
      if (error) throw error;
      
      if (data.user) {
        setUser(data.user);
        // Refresh to load profile/stats (created with subscription_type free or trial)
        await refreshUserData(data.user);
      }
    } catch (error) {
      console.error('Error signing up:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      console.log('Starting sign out process...');
      
      // Clear Supabase session
      await supabase.auth.signOut();
      
      // Clear React state
      setUser(null);
      setUserProfile(null);
      setUserStats(null);
      
      // Clear ALL localStorage data (Safari-specific fix)
      localStorage.clear();
      
      // Clear sessionStorage as well (Safari-specific)
      sessionStorage.clear();
      
      // Clear any cookies (if possible)
      document.cookie.split(";").forEach(function(c) { 
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
      });
      
      console.log('Sign out completed successfully - all data cleared');
      
      // Show success notification
      alert('You have been successfully signed out!');
    } catch (error) {
      console.error('Error signing out:', error);
      
      // Even if Supabase signOut fails, clear local state
      setUser(null);
      setUserProfile(null);
      setUserStats(null);
      
      // Clear ALL data anyway
      localStorage.clear();
      sessionStorage.clear();
      
      throw error;
    }
  };

  const updateUserProfile = async (profile: Partial<UserProfile>) => {
    try {
      if (user) {
        console.log('Updating user profile:', profile);
        const updatedProfile = await supabaseHelpers.upsertUserProfile({
          ...profile,
          user_id: user.id,
          updated_at: new Date().toISOString()
        });
        console.log('Profile updated successfully:', updatedProfile);
        setUserProfile(updatedProfile);
        
        // Don't trigger refreshUserData immediately to avoid race conditions
        // The profile is already updated, so we don't need to fetch it again
      }
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  };

  const updateUserStats = async (stats: Partial<UserStats>) => {
    try {
      if (user) {
        const updatedStats = await supabaseHelpers.upsertUserStats({
          ...stats,
          user_id: user.id,
          updated_at: new Date().toISOString()
        });
        setUserStats(updatedStats);
        return updatedStats;
      }
    } catch (error) {
      console.error('Error updating user stats:', error);
      throw error;
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        console.log('Initializing auth...');
        
        // Check if Supabase is properly configured
        if (!process.env.REACT_APP_SUPABASE_URL || !process.env.REACT_APP_SUPABASE_ANON_KEY) {
          console.error('❌ Supabase environment variables not configured');
          setLoading(false);
          return;
        }


        
        
        // Clear only corrupted session data, preserve Supabase session
        console.log('Checking for corrupted session data...');
        const hasCorruptedSession = localStorage.getItem('realestart_user_id') && 
                                   !localStorage.getItem('sb-tfkhydguwdnojvaoiyqo-auth-token');
        
        if (hasCorruptedSession) {
          console.log('Detected corrupted session data, cleaning up...');
          // Only clear our app-specific data, not Supabase session data
          localStorage.removeItem('realestart_user_stats');
          localStorage.removeItem('realestart_user_mode');
          localStorage.removeItem('realestart_user_profile');
          localStorage.removeItem('realestart_user_id');
        }

        // Get initial session with timeout - increased for Safari compatibility
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Auth initialization timeout')), 25000)
        );
        
        try {
          const { data: { session } } = await Promise.race([sessionPromise, timeoutPromise]) as any;
          console.log('Session data:', session);
          setUser(session?.user ?? null);

          if (session?.user) {
            console.log('User found in session:', session.user);
            setUser(session.user);
            console.log('About to call refreshUserData...');
            await refreshUserData(session.user);
            console.log('refreshUserData completed');
          } else {
            console.log('No user in session');
            // Clear any existing profile/stats data
            setUserProfile(null);
            setUserStats(null);
          }
        } catch (sessionError) {
          console.warn('Session fetch failed, continuing without session:', sessionError);
          setUser(null);
          setUserProfile(null);
          setUserStats(null);
        }

        // Listen for auth changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event, session) => {
            try {
              console.log('Auth state changed:', event, session?.user);
              setUser(session?.user ?? null);
              
              if (session?.user) {
                await refreshUserData(session.user);
              } else {
                // Clear profile/stats when user signs out
                setUserProfile(null);
                setUserStats(null);
              }
            } catch (error) {
              console.error('Error in auth state change handler:', error);
              // Don't let auth state changes crash the app
            }
          }
        );

        setLoading(false);
        return () => subscription.unsubscribe();
      } catch (error) {
        console.error('Error initializing auth:', error);
        if (error instanceof Error && error.message?.includes('timeout')) {
          console.warn('Auth loading timeout reached, continuing with limited functionality');
          // Don't redirect on timeout - allow user to continue
          setUser(null);
          setUserProfile(null);
          setUserStats(null);
        }
        setLoading(false);
      }
    };

    initializeAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Remove refreshUserData dependency to prevent infinite loop

  const value = {
    user,
    userProfile,
    userStats,
    loading,
    darkMode,
    setDarkMode,
    signIn,
    signUp,
    signOut,
    updateUserProfile,
    updateUserStats,
    refreshUserData
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
